import {
	createContext,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { Manager, Socket } from 'socket.io-client';
import { toast } from 'sonner';

import { keepsTokenOnLimit, losesToken } from '../lib/offers-tv-move';
import { useTheme } from './ThemeContext';
import { useApp } from './app';

type statusSocket =
	| 'connecting'
	| 'connected'
	| 'disconnected'
	| 'reconnected'
	| 'business_not_found'
	| 'token_invalid'
	| 'agent_not_found'
	| 'limit_single_connection';

interface SocketIOProps {
	updateStatusSocket: (statusSocket: statusSocket) => void;
	statusSocket: statusSocket;
	socket: Socket;
}

const socketIOContext = createContext<SocketIOProps>({} as any);

const uri = import.meta.env.VITE_API_URL;
const path = '/socket';

const manager = new Manager(uri, {
	path,
	secure: true,
	autoConnect: false,
	reconnection: true,
	reconnectionAttempts: Infinity,
	reconnectionDelay: 1000,
	reconnectionDelayMax: 10000,
});

const socket = manager.socket('/consulta-preco');

export function SocketIOProvider({ children }: { children: React.ReactNode }) {
	const {
		auth,
		updateTypeConnection,
		updateToken,
		updateBusiness,
		updateAuthId,
	} = useApp();
	const timeout = useRef<ReturnType<typeof setInterval> | null>(null);
	const hasConnectedOnce = useRef(false);
	const { updateTheme } = useTheme();

	const [statusSocket, setStatusSocket] =
		useState<statusSocket>('disconnected');
	const statusErrorNoReset = ['agent_not_found'];
	const statusErrorReset = [
		'business_not_found',
		'token_invalid',
		'limit_single_connection',
	];

	useEffect(() => {
		if ([...statusErrorNoReset, ...statusErrorReset].includes(statusSocket)) {
			socket.disconnect();
		}
	}, [statusSocket]);

	// Heartbeat: detecta conexão stale mesmo sem evento disconnect
	useEffect(() => {
		if (statusSocket !== 'connected') return;
		const heartbeat = setInterval(() => {
			if (!socket.connected) {
				setStatusSocket('disconnected');
				socket.connect();
			}
		}, 30_000);
		return () => clearInterval(heartbeat);
	}, [statusSocket]);

	useLayoutEffect(() => {
		socket.auth = auth;
		if (auth.token) {
			const id = localStorage.getItem('authId');
			if (id) {
				socket.auth.id = id;
			}
			setStatusSocket('connecting');
			socket.connect();
		} else {
			hasConnectedOnce.current = false;
			setStatusSocket('disconnected');
			if (socket.connected) {
				socket.disconnect();
			}
		}

		// Remove listeners anteriores antes de adicionar novos (evita acúmulo)
		socket.off('connect');
		socket.off('connected');
		socket.off('error');
		socket.off('disconnect');
		socket.off('reconnect');

		socket.on('connect', () => {
			if (timeout.current) clearInterval(timeout.current);
		});

		socket.on('error', (data) => {
			const functions = data.data?.functions;
			if (losesToken(data.code, functions)) {
				updateToken();
				toast.error(data.message);
			}
			if (
				data.code === 'agent_not_found' ||
				keepsTokenOnLimit(data.code, functions)
			) {
				updateTypeConnection(data.data.typeConnection, data.data.functions);
				updateBusiness(data.data);
				if (timeout.current) clearInterval(timeout.current);
				timeout.current = setInterval(() => {
					console.log('RECONNECTING...');
					socket.disconnect();
					socket.connect();
				}, 5000);
			}
			updateStatusSocket(data.code);
			toast.error(data.message);
		});

		socket.on('connected', (data) => {
			updateBusiness({
				cnpj: data.data.cnpj,
				fantasia: data.data.fantasia,
				saleId: data.data.saleId,
			});
			const isPurchaseOrder = data.data.functions?.includes('purchase_order');
			updateTheme(isPurchaseOrder ? null : (data.data.theme ?? null));
			updateAuthId(data.data.authId);
			updateTypeConnection(data.data.typeConnection, data.data.functions);
			updateStatusSocket('connected');
			if (!hasConnectedOnce.current) {
				hasConnectedOnce.current = true;
				toast.success('Conectado com sucesso! Pode iniciar as consultas!');
			} else {
				toast.info('Reconectado!', { duration: 2000 });
			}
		});

		socket.on('disconnect', (reason) => {
			// 'io client disconnect' = desconexão intencional (token inválido, etc.)
			if (reason === 'io client disconnect') return;
			updateStatusSocket('disconnected');
		});

		socket.on('reconnect', () => {
			updateStatusSocket('reconnected');
		});

		return () => {
			socket.disconnect();
			socket.off('connected');
			socket.off('error');
			socket.off('connect');
			socket.off('disconnect');
			socket.off('reconnect');
		};
	}, [auth]);

	function updateStatusSocket(statusSocket: statusSocket) {
		setStatusSocket(statusSocket);
	}

	return (
		<socketIOContext.Provider
			value={{ updateStatusSocket, statusSocket, socket }}
		>
			{children}
		</socketIOContext.Provider>
	);
}

export function useSocketIO() {
	return useContext(socketIOContext);
}
